import React, { useContext, useEffect, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { UserContextProps } from '../Contexts/UserContext';
import UserContext from '../Contexts/UserContext';
import type { LocalStorageContextProps } from '../Contexts/LocalStorageContext';
import LocalStorageContext from '../Contexts/LocalStorageContext';
import type { ModeContextProps } from '../Contexts/ModeContext';
import ModeContext from '../Contexts/ModeContext';
import Mode from '../Enums/Mode';
import type { SystemContextProps } from '../Contexts/SystemContext';
import SystemContext from '../Contexts/SystemContext';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import AppTheme from '../Enums/AppTheme';
import type { SettingsContextProps } from '../Contexts/SettingsContext';
import SettingsContext from '../Contexts/SettingsContext';
import { updateLastActivity } from '../Utils/LastActivity';
import { sha256 } from 'shouldve-been-js';
import { useTitle } from 'shouldve-been-react';
import { ErrorMessage, PinCodeInput } from 'shouldve-been-mui';

function CreatePinPage(): JSX.Element {

    useTitle('K-Fleet Experience - Create PIN');

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const modeContext: ModeContextProps = useContext<ModeContextProps>(ModeContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const settingsContext: SettingsContextProps = useContext<SettingsContextProps>(SettingsContext);

    const currentTheme: Theme = useTheme();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    function updateTheme(theme: AppTheme): void {

        settingsContext.setTheme(theme);
        localStorageContext.localStorageService.setTheme(theme);
    }

    async function handleComplete(pin: string): Promise<void> {

        const hashedPin: string | null = await sha256((userContext.currentUser?.username ?? '') + '_' + pin);

        if (hashedPin === null) {
            setErrorMessage('PIN could not be saved');
            return;
        }

        userContext.setHashedPin(hashedPin);
        localStorageContext.localStorageService.setHashedPin(hashedPin);
        updateLastActivity(userContext, localStorageContext);

        modeContext.setMode(Mode.MAIN);
        localStorageContext.localStorageService.setMode(Mode.MAIN);
    }

    return (
        <div style={{
            padding: '5px',
            height: windowSize[1],
            overflow: 'auto',
        }}>
            <div style={{
                textAlign: 'right',
                padding: '10px 10px 0px 0px',
            }}>
                <table style={{
                    marginLeft: 'auto',
                    marginTop: '10px',
                }}>
                    <tbody>
                        <tr>
                            <td style={{
                                color: currentTheme.palette.text.primary,
                            }}>
                                Dark Mode
                            </td>
                            <td>
                                <FormControl>
                                    <Switch
                                        checked={settingsContext.theme === AppTheme.DARK_MODE}
                                        onChange={(e: React.ChangeEvent): void => updateTheme((e.target as HTMLInputElement).checked ? AppTheme.DARK_MODE : AppTheme.LIGHT_MODE)}
                                        color='primary'
                                    />           
                                </FormControl>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div 
                style={{
                    textAlign: 'center',
                    marginTop: '20px',
                }}
            >
                <img 
                    src={systemContext.logoUrl ?? ''}
                    alt='Logo' 
                    width='80px'
                    style={{
                        marginBottom: '20px',
                    }}
                />
                <div style={{
                    marginBottom: '20px',
                    fontSize: '20px',
                    fontWeight: 500,
                    lineHeight: '170%',
                }}>
                    <span style={{
                        fontSize: '18px',
                        fontWeight: 500,
                        color: currentTheme.palette.text.primary,
                    }}>
                        Connected to:
                    </span>
                    <br />
                    <span style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: currentTheme.palette.text.primary,
                    }}>
                        {systemContext.systemName ?? ''}
                    </span>
                </div>
                <div style={{
                    marginBottom: '20px',
                    fontSize: '20px',
                    fontWeight: 500,
                    lineHeight: '170%',
                }}>
                    <span style={{
                        fontSize: '18px',
                        fontWeight: 500,
                        color: currentTheme.palette.text.primary,
                    }}>
                        Current User:
                    </span>
                    <br />
                    <span style={{
                        fontSize: '20px',
                        fontWeight: 600,
                        color: currentTheme.palette.text.primary,
                    }}>
                        {userContext.currentUser?.username ?? 'ERROR'}
                    </span>
                </div>
                <PinCodeInput
                    length={4}
                    onComplete={handleComplete}
                />
                <Typography style={{
                    color: currentTheme.palette.text.primary,
                    marginTop: '40px',
                }}>
                    Create a PIN code to be able to sign in even when offline next time
                </Typography>
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                />
            </div>
        </div>
    );
}

export default CreatePinPage;